import React, { useCallback, useMemo, useState } from "react";
import styles from "./CustomFieldFilterLayout.module.css";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { Control, FieldErrors, useFieldArray } from "react-hook-form";
import { RenderCustomFieldOperator } from "../CustomFieldFilterBuilder/CustomFieldFilterRenderUtils";
import { CustomFieldFilterFormRenderer } from "../../CustomFields/CustomFieldEditRenderer";
import { CheckboxRound } from "../../../common/formfields/CheckboxFormField/CheckboxFormField";
import { CustomFieldFilterBuilder, ICustomFieldSearchForm } from "../CustomFieldFilterBuilder/CustomFieldFilterBuilder";
import { OverlayInfo } from "../../../common/misc/OverlayInfo/OverlayInfo";
import { CustomFieldSavedFilters } from "../CustomFieldSavedFilters/CustomFieldSavedFilters";
import { CustomField } from "../../../api/CustomFields/CustomFields";
import { isValueCustomFieldSearchOperator } from "../CustomFieldFilterBuilder/CustomFieldFilterUtils";
import { FoldableFilters } from "../../../common/sidebar/filtersidebar/GenericFilters/common/FoldableFilters";
import { CustomTypeFilters } from "../../../api/CustomTypes/CustomTypes";

export type CustomFieldFilterLayoutForm = {
  customFieldFilters?: ICustomFieldSearchForm[] | null;
};
export interface CustomFieldFilterLayoutProps<Form> {
  control: Form extends CustomFieldFilterLayoutForm ? Control<Form> : never;
  errors: Form extends CustomFieldFilterLayoutForm ? FieldErrors<Form> : never;
  customFieldsDict: Record<string, CustomField>;
  entityTypes: CustomTypeFilters["entityTypes"];
}
export const CustomFieldFilterLayout = <Form extends CustomFieldFilterLayoutForm>({
  control,
  errors,
  customFieldsDict,
  entityTypes,
}: CustomFieldFilterLayoutProps<Form>) => {
  const [showCustomFilters, setShowCustomFilters] = useState(false);

  const { fields, update, insert, remove, replace } = useFieldArray({
    control,
    name: "customFieldFilters", // unique name for your Field Array
    keyName: "field-id",
    // shouldUnregister: true,
  });

  const sortedActiveFields = useMemo(() => fields?.filter((f) => f.showInSidebar) ?? [], [fields]);

  // console.log("fields", fields, "sortedActiveFields", sortedActiveFields);

  const getIndexForId = useCallback(
    (id: string) => {
      return fields.findIndex((f) => f.id === id);
    },
    [fields]
  );

  const onCreate = useCallback(
    (data: ICustomFieldSearchForm) => {
      const index = getIndexForId(data.id);
      if (index !== -1) {
        update(index, { ...data });
      } else {
        insert(0, { ...data });
      }
      // await writeCustomFieldFilters({ ...savedFilters, [data.id]: data });
    },
    [getIndexForId, insert, update]
  );

  const onEditCallback = useCallback((filter: ICustomFieldSearchForm) => {
    setDefaultValues({ ...filter });
    setShowCustomFilters(true);
  }, []);

  const onUpdate = useCallback(
    (field: ICustomFieldSearchForm) => {
      const index = getIndexForId(field.id);
      if (index !== -1) {
        update(index, { ...field, active: !field.active });
      }
    },
    [getIndexForId, update]
  );

  const onReplace = useCallback(
    (fields: ICustomFieldSearchForm[]) => {
      replace(fields);
    },
    [replace]
  );

  const onDeleteCallback = useCallback(
    (id: string) => {
      const index = getIndexForId(id);
      if (index !== -1) {
        remove(index);
      }
    },
    [getIndexForId, remove]
  );

  const [defaultValues, setDefaultValues] = useState<ICustomFieldSearchForm>();

  return (
    <FoldableFilters
      label={
        <div className="flex row-nowrap align-center gap-5 ellipsisContainer">
          <span>Custom filters</span>
          <OverlayInfo icon="info">
            <div>
              <p>
                Create custom filters to search for specific custom field values. You can create simple filters or logic
                trees.
              </p>
            </div>
          </OverlayInfo>
        </div>
      }
      controls={
        <div className="flex row-nowrap align-center gap-5">
          <button
            type="button"
            className="btn btn-xs btn-primary"
            title="Create custom filter"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDefaultValues(undefined);
              setShowCustomFilters(true);
            }}
          >
            <LucideIcon name="plus" /> Create
          </button>
          <CustomFieldSavedFilters
            savedFilters={fields}
            onReplace={onReplace}
            onUpdate={onUpdate}
            onEditCallback={onEditCallback}
            onDeleteCallback={onDeleteCallback}
          />
          {/* Contains the build modal */}
          <CustomFieldFilterBuilder
            show={showCustomFilters}
            setShow={setShowCustomFilters}
            defaultValues={defaultValues}
            onCreate={onCreate}
            customFieldsDict={customFieldsDict}
            entityTypes={entityTypes}
          />
        </div>
      }
      defaultOpen={!!sortedActiveFields.length}
    >
      <div className="flex col-nowrap" style={{ width: "100%", height: "fit-content" }}>
        <div className={styles.layoutContainer}>
          <div className={styles.layoutBody}>
            {sortedActiveFields && !!sortedActiveFields.length ? (
              sortedActiveFields.map((field) => {
                const index = getIndexForId(field.id);
                return (
                  <RenderCustomFieldFilter
                    field={field}
                    onUpdate={onUpdate}
                    onEditCallback={() => onEditCallback(field)}
                    key={field.id}
                  >
                    {field.filterQuery?.customField && field.filterQuery.operator ? (
                      <CustomFieldFilterFormRenderer
                        control={control}
                        errors={errors}
                        id={`customFieldFilters.${index}.filterQuery.value`}
                        operator={field.filterQuery.operator}
                        customField={field.filterQuery?.customField}
                        label={""}
                        uncontained
                      />
                    ) : (
                      <></>
                    )}
                  </RenderCustomFieldFilter>
                );
              })
            ) : (
              <span style={{ color: "var(--gray-400)", fontSize: "0.9em" }}>
                You haven&apos;t created any custom filters yet. Click <b>&quot;Create&quot;</b> to add your first
                filter and start customizing your search!
              </span>
            )}
          </div>
        </div>
      </div>
    </FoldableFilters>
  );
};

interface RenderCustomFieldFilterProps {
  field: ICustomFieldSearchForm;
  onUpdate: (field: ICustomFieldSearchForm) => void;
  onEditCallback: () => void;
  children?: React.ReactNode;
}
const RenderCustomFieldFilter = ({ field, onUpdate, onEditCallback, children }: RenderCustomFieldFilterProps) => {
  return (
    <div
      className={styles.customFieldFilterContainer}
      style={{
        borderTop: "0px solid var(--gray-300)",
        borderRadius: 4,
        padding: 2.5,
        // ...(!field.active && { filter: "opacity(50%)" }),
        ...(field.type === "tree" && !field.active
          ? { filter: "opacity(50%)", borderColor: "var(--gray-300)" }
          : field.type === "flat" &&
            field.filterQuery?.operator &&
            !isValueCustomFieldSearchOperator(field.filterQuery.operator) &&
            !field.active
          ? { filter: "opacity(50%)", borderColor: "var(--gray-300)" }
          : field.filterQuery?.operator &&
            isValueCustomFieldSearchOperator(field.filterQuery.operator) &&
            (!field.filterQuery?.value ||
              (field.filterQuery &&
                Array.isArray(field.filterQuery.value) &&
                field.filterQuery.value.length === 0)) && {
              filter: "opacity(50%)",
              borderColor: "var(--gray-300)",
            }),
      }}
    >
      <div className="flex row-nowrap align-center gap-5">
        <div className="flex row-nowrap align-center" style={{ gap: 2.5, marginBottom: 2.5, width: "100%" }}>
          <div className="flex align-center ellipsisContainer" style={{ height: "fit-content", width: "fit-content" }}>
            <span
              style={{
                fontWeight: 600,
                lineHeight: "16px",
              }}
            >
              {field.type === "tree" && (
                <>
                  <LucideIcon name="network" style={{ color: "var(--gray-400)" }} />{" "}
                </>
              )}
              {field.name}
            </span>
          </div>
          {/* <RenderCustomFieldFilterType type={field.type} /> {field.type === "flat" ? "Simple" : "Complex"} filter */}
          <div className="flex row-nowrap align-center gap-5" style={{ marginLeft: "auto" }}>
            <button className="btn btn-xs btn-default" onClick={onEditCallback} title="Edit filter">
              <LucideIcon name="settings-2" /> Edit
            </button>
            {(field.type === "tree" ||
              (field.type === "flat" &&
                field.filterQuery?.operator &&
                !isValueCustomFieldSearchOperator(field.filterQuery.operator))) && (
              <>
                <span
                  style={{
                    color: "var(--primary)",
                    background: "transparent",
                    fontSize: "0.9rem",
                    lineHeight: "1rem",
                  }}
                >
                  Apply
                </span>
                <CheckboxRound
                  checked={field.active}
                  onChange={() => {
                    onUpdate(field);
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex row-nowrap align-center" style={{ gap: 2 }}>
        {field.type === "flat" && field.filterQuery && field.filterQuery.operator && (
          <>
            {/* <span style={{ color: "var(--gray-400)", fontSize: "1.2em" }}>ƒ(𝑥)</span>{" "} */}
            <RenderCustomFieldOperator
              operator={field.filterQuery.operator}
              style={{ width: 20, height: 20, color: "var(--gray-400)" }}
            />
          </>
        )}
        {children}
      </div>
    </div>
  );
};
